/**
 * 新版本登录库
 * @author colynchen
 * @created 2019-03-20
 */
import { invoke, on } from '@tencent/vft-atom/lib/tenvideo';

import cookie, { parseCookieString } from '../cookie';
import env from '../env';
import { buildUrl } from '../url';
import { request } from '../request';
import util from '../util';
import { version } from '../package.json';

import * as reporter from './report';
import monitor from './monitor';
import LoginProxy from './LoginProxy';
import { clearRootDomainCookies, clearPageCookies, isNativeLogin } from './helper';
import {
  loginType,
  syncCookies,
  refreshAuthToken,
  skey2Vusession,
  clearCookie,
  authLogout,
  getUin,
  getSkey,
  getToken,
  getDomain,
  buildCSRF,
  innerToken2tenvideo,
  requestQQNativeAuth,
  requestQQLogin,
} from './lib';

const ON_APP_LOGIN = 'onActionLoginFinish';
const ON_APP_LOGOUT = 'onActionLogoutFinish';
const ON_APP_SWITCH_LOGIN = 'onSwitchLoginStateResponse';
const APP_LOGIN_CHANGED_EVENTS = [ON_APP_LOGIN, ON_APP_LOGOUT, ON_APP_SWITCH_LOGIN];
const perf = monitor.getPerf();
const UA = navigator.userAgent;

let isInitCall = false;
let isInitReady = false;
window.addEventListener('load', () => {
  setTimeout(() => {
    if (!isInitCall) {
      console && console.warn('`login.init()` should be called before your business logic, in case something goes wrong.');
    }
  }, 0);
});

// 初始化回调队列
const ONREADY_QUEUES = [];
const setReady = function () {
  if (ONREADY_QUEUES.length > 0) {
    ONREADY_QUEUES.forEach((fn) => {
      if (typeof fn === 'function') {
        fn();
      }
    });
    ONREADY_QUEUES.length = 0;
  }
  isInitReady = true;
  reporter.report({
    f_event: reporter.EVENT_INIT_READY,
  });
  perf.timeEnd('init_time');
  perf.report({
    modId: '登录调用量',
    tags: {
      device: env.device,
      app: env.app,
      loginType: loginType(),
    },
  });
};

const loginConfig = {};
/**
 * 支持多业务登录
 * @param {object} params 应用参数
 * @param {number} params.qqAppid QQ登录的appid
 * @param {number} params.wxAppid 微信登录的appid
 * @param {number} params.title   登录页标题
 */
function setConfig({ qqAppid, wxAppid, title } = {}) {
  loginConfig.qqAppid = qqAppid;
  loginConfig.wxAppid = wxAppid;
  loginConfig.title = title;
}

/**
 * 登录库初始化
 * @param {Function} callback 初始化完成回调
 */
function init(callback) {
  if (isInitReady) {
    return typeof callback === 'function' && callback();
  }

  ONREADY_QUEUES.push(callback);

  // 避免重复调用 init 方法
  if (isInitCall) return;
  isInitCall = true;

  perf.report({
    modId: '登录调用量',
    metrics: {
      init: 1,
    },
    tags: {
      device: env.device,
      app: env.app,
    },
  });
  perf.timeStart('init_time');

  // APP 环境下同步 cookie，但不做任何处理
  if (env.isLocalPkg) {
    cookie.enableProxy();
    _syncAppCookies().then(setReady);
    APP_LOGIN_CHANGED_EVENTS.forEach((e) => {
      on(e, () => {
        _syncAppCookies();
      });
    });
    return;
  }

  if (env.isApp) {
    // 腾讯视频应用
    return setReady();
  }

  // 背景: QQ浏览器11.6.5版本开始支持登录态打通
  // 仅支持在长视频播放场景种视频票据到qq.com, 其他场景登录不会种视频票据,
  // 但票据只要换出来后是全局共享的,会导致搜索场景访问的H5页面登录态污染,无法正常登录
  // !!优先判断非高级框架场景，清除终端种的票据
  if (env.isMqb && !env.isTenvideoUnion) {
    clearRootDomainCookies();
  }

  // 内容分发登录态打通场景，默认清掉当前页面cookie信息，避免登录态污染
  if (isNativeLogin()) {
    clearPageCookies();
    // iframe 场景调用后台 auth_logout 接口会导致在 iframe 内取到空 cookie
    if (top !== self) {
      setReady();
      return;
    }
    // 调用后台退出登录接口清除 video.qq.com 票据
    return authLogout('qq').then(setReady);
  }

  // 1. 降域处理
  if (document.domain !== 'qq.com') {
    window.console && console.log('[login] 尝试将页面降域到 qq.com');
    try {
      document.domain = 'qq.com';
    } catch (_) {
    }
  }

  // 存在有效的mpcookie，认为是来自于腾讯视频小程序，需要同步小程序的登录态
  // 其他小程序不做登录态同步，直接走h5逻辑
  const mpCookie = decodeURIComponent(util.getUrlParam('mpcookie'));
  if (env.isWeChatMiniProgram && mpCookie) {
    const cookieParam = parseCookieString(util.filterXSS(mpCookie));
    const setLoginType = cookieParam && cookieParam.main_login;
    if (setLoginType) {
      clearRootDomainCookies();

      clearCookie(setLoginType, () => {
        Object.keys(cookieParam).forEach((key) => {
          cookie.set(key, cookieParam[key], {
            domain: 'qq.com',
            path: '/',
          });
        });

        refreshAuthToken(setLoginType, () => {
          Object.keys(cookieParam).forEach((key) => {
            cookie.remove(key, {
              domain: 'qq.com',
              path: '/',
            });
          });
          syncCookies(setReady);
        });
      });
      return;
    }
  }

  if (UA.match(/(weishi|msdk|qqsports|qqmusic)/i) || util.getUrlParam('msdkEncodeParam').length > 0) {
    // 支持微视、腾讯体育、游戏走票据转换
    // 第三方场景无须续期票据
    innerToken2tenvideo(() => {
      syncCookies(setReady);
    });
  } else {
    // 移除 qq.com 域下的微信登录态
    // 来源游戏业务的 cookie 污染，特坑！！！
    clearRootDomainCookies();
    // 同步 cookie 信息
    syncCookies(() => {
      // 外站 PTLogin 登录态进入视频页，无视频票据 vuserid 和 vusession 情况下
      // 通过转换票据接口，种视频登录态
      // 旧版QQ登录态，如果没有视频票据，需要同步登录态
      if (isPTLogin() && (!cookie.get('vuserid') || !cookie.get('vusession'))) {
        skey2Vusession(setReady);
      } else {
        // 以下情况均为无效登录态，需要清除登录态信息
        // 1. 没有QQ或微信登录信息，但有视频票据
        // 2. 是QQ互联登录态，也有旧的视频票据，会导致登录态混乱
        // 3. 当前是微信登录态且存在互联登录态时
        if ((!isLogin() && (cookie.get('vuserid') || cookie.get('vusession')))
          || (isWXLogin() && (cookie.get('vqq_vuserid') || cookie.get('vqq_vusession')))
          || (isNewQQLogin() && cookie.get('vuserid') && cookie.get('vusession'))
        ) {
          clearCookie(() => {
            syncCookies(setReady);
          });
        } else if (isLogin()) {
          // 已经登录，则续期票据
          const lastTime = +cookie.get('last_refresh_time');
          const vuserid = cookie.get('last_refresh_vuserid');
          if (+new Date() - lastTime >= 300000 || !getNick() || !getAvatar() || vuserid !== getVuid()) { // 5分钟/刷新票据
            // 续期后需要再次同步 cookie 信息
            refreshAuthToken(() => {
              syncCookies(setReady);
            });
          } else {
            setReady();
          }
        } else {
          setReady();
        }
      }
    });
  }
}

/**
 * 监听初始化完成
 * @param {Function} callback 回调函数
 */
function onInit(callback) {
  if (isInitReady) {
    typeof callback === 'function' && callback();
  } else {
    ONREADY_QUEUES.push(callback);
  }
}

/**
 * 是否已经登录
 * 不仅单纯仅判断是否有 main_login 就代表已登录
 * 通过 cookie 中是否存在登录态对应的存在关键信息来决定
 * @param {String} type 可获取指定登录态类型的登录态
 */
export function isLogin(type) {
  if (!type) type = loginType();

  const vuserid = isNewQQLogin() ? cookie.get('vqq_vuserid') : cookie.get('vuserid');
  const vusession = isNewQQLogin() ? cookie.get('vqq_vusession') : cookie.get('vusession');

  if (!type || !vuserid || !vusession) {
    // 不存在视频票据时未登录
    return false;
  }

  if (type === 'wx') {
    return isWXLogin();
  } if (type === 'qq') {
    return isQQLogin();
  }

  return true;
}

/**
 * 判断是否微信登录态
 */
export function isWXLogin() {
  if (loginType() === 'wx' && cookie.get('appid') && cookie.get('openid') && cookie.get('access_token')) {
    return true;
  }
  return false;
}

/**
 * 判断是否 QQ 登录态
 */
export function isQQLogin() {
  return isPTLogin() || isNewQQLogin();
}

/**
 * 是否是旧版 PTLogin 登录态
 */
export function isPTLogin() {
  if (loginType() === 'wx') return false;
  if (!isNewQQLogin() && ((cookie.get('uin') && cookie.get('skey'))
    || (cookie.get('luin') && cookie.get('lskey')))) {
    return true;
  }
  return false;
}

/**
 * 是否是新的互联登录态
 */
export function isNewQQLogin() {
  if (loginType() === 'wx') return false;
  if (loginType() === 'qq' && cookie.get('vqq_appid') && cookie.get('vqq_openid') && cookie.get('vqq_access_token')) {
    return true;
  }
  return false;
}

/**
 * 校验强登录态
 * @param {Function} callback
 */
export function isStrictLogin(callback) {
  if (typeof callback !== 'function') return;
  if (!isLogin()) {
    return callback(false);
  }

  const params = {};
  if (loginType() === 'wx') {
    params.type = 3;
    params.appid = cookie.get('appid');
    params.openid = cookie.get('openid');
    params.access_token = cookie.get('access_token');
  } else if (isNewQQLogin()) { // 新的QQ互联登录态
    params.type = 6;
    params.vqq_appid = cookie.get('vqq_appid');
    params.vqq_openid = cookie.get('vqq_openid');
    params.vqq_access_token = cookie.get('vqq_access_token');
  } else if (isPTLogin()) {
    params.type = 1;
    params.uin = getUin();
    params.skey = getSkey();
  }

  checkUser(params).then(() => {
    callback(true);
  })
    .catch(() => {
      callback(false);
    });
}

/**
 * 检验账号登录态、账号一致性等
 * @example
 * 1、qq强登录校验 {type:1,uin,'',skey:''}
 * 2、微信登录态校验 {type:3,appid:'',openid:'',access_token:''}
 * 3、微信账号一致性校验 {type:4,appid1:'',openid1:'',appid2:'',openid2:''}
 * 4、QQ互联登录态校验 {type:6,vqq_appid:'',vqq_openid:'',vqq_access_token:''}
 * 5、QQ登录态一致性校验 {type:7,appid1:'',openid1:'',appid2:'',openid2:''}
 * 6、QQ号与互联登录态一致性校验 {type:7,appid1:'',openid1:'',uin:''}
 */
export function checkUser(params, onSuccess, onFail) {
  return new Promise((resolve, reject) => {
    if (typeof onSuccess !== 'function') {
      onSuccess = resolve;
      onFail = reject;
    }
    return request('//vip.video.qq.com/fcgi-bin/comm_cgi?name=check_login&otype=xjson', params, {
      timeout: 60000,
    }).then((res) => {
      if (res && res.result && res.result.code === 0) {
        onSuccess && onSuccess(res);
      } else {
        onFail && onFail(res);
      }
    })
      .catch((e) => {
        onFail && onFail(e);
      });
  });
}

/**
 * 退出登录
 */
export function logout(callback) {
  if (env.isApp) {
    invoke('logout', {
      needBack: false,
    }, (jsonTxt) => {
      try {
        const res = JSON.parse(jsonTxt);
        if (res && res.errCode === 0) {
          callback && callback(true);
        } else {
          callback && callback(false);
        }
      } catch (e) {
        callback && callback(false);
      }
    });
  } else {
    clearCookie(() => {
      callback && callback(true);
    });
  }
  reporter.report({
    f_event: reporter.EVENT_INIT_LOGOUT,
  });
}

/**
 * 拉登录
 * @param {Object} param 登录参数
 * @param {Object} param.type 登录类型，qq/wx
 * @param {Object} param.url 登录回跳地址
 * @param {Boolean} clear 是否清除登录态
 */
export function openLogin(param = { type: '' }, clear = false) {
  if (typeof param === 'string') {
    param = {
      type: param,
    };
  } else if (Object.prototype.toString.call(param) !== '[object Object]') {
    param = {};
  }
  if (!param.url) {
    param.url = location.href;
  }

  reporter.report({
    f_event: reporter.EVENT_INIT_LOGIN,
    f_type: param.type,
  });

  // 存在mpcookie，认为是来自于腾讯视频小程序，同步登录态；其他小程序不做登录态同步，直接走h5逻辑
  if (env.isWeChatMiniProgram && util.getUrlParam('mpcookie')) {
    util.loadScript(
      'jweixin',
      '//res.wx.qq.com/open/js/jweixin-1.3.2.js',
      () => {
        // eslint-disable-next-line
        wx.miniProgram.navigateTo({
          url: `/preload_vip/login/index?isLogin=${isLogin()}`,
        });
      },
    );
  } else if (env.isApp) {
    invoke('actionLogin', {
      type: param.type || 'tv',
    }).then((res) => {
      if (res && res.type) { // 登录或者退出登录成功
        location.href = param.url;
      }
    });
  } else {
    if (param.type === 'wx') {
      param.back = 1;
    }
    if (clear) {
      logout(_doLogin.bind(this, param));
    } else {
      _doLogin(param);
    }
  }
}

/**
 * 双登录页登录
 * @param {Object} param 登录参数
 */
function _doLogin(param) {
  if (param.url) {
    param.ru = param.url;
    delete param.url;
  }
  // 清除上次续期的时间
  cookie.remove('last_refresh_time', {
    domain: getDomain(),
    path: '/',
  });
  cookie.remove('last_refresh_vuserid', {
    domain: getDomain(),
    path: '/',
  });
  location.href = buildUrl('//film.qq.com/weixin/login.html', {
    qqAuth: util.getUrlParam('qqAuth'),
    ...loginConfig,
    ...param,
  }, true);
}

/**
 * 获取主登录态用户信息
 * @returns {(object|null)}
 */
export function getMainUserInfo() {
  if (!isLogin()) return Promise.resolve(null);

  return Promise.resolve({
    type: loginType(),
    nick: getNick(),
    headurl: getAvatar(),
    id: getUin() || getUid(),
  });
}

/**
 * 获取主账号 cookie 信息
 * @returns {(object|null)}
 */
export function getMainCookie() {
  if (env.isLocalPkg && cookie.isProxyEnabled()) {
    return cookie.getCookies();
  }
  const _cookies = parseCookieString(document.cookie);
  return _cookies;
}

/**
 * 获取用户昵称
 * @returns {string}
 */
function getNick() {
  if (!isLogin()) return '';
  const nick = cookie.get(`${loginType()}_nick`);
  try {
    return decodeURIComponent(nick);
  } catch (error) {
    return nick;
  }
}

/**
 * 获取用户头像
 * @returns {string}
 */
function getAvatar() {
  if (!isLogin()) return '';
  try {
    return decodeURIComponent(cookie.get(`${loginType()}_head`));
  } catch (e) {
    return '';
  }
}

/**
 * 获取登录账号 id
 * @returns {string|number}
 */
export function getUid() {
  const type = loginType();
  if (!type) return '';
  if (type === 'qq') {
    return isNewQQLogin() ? cookie.get('vqq_openid') : getUin();
  }
  return cookie.get('openid');
}

/**
 * 获取视频 vuserid
 * @returns {string}
 */
export function getVuid() {
  return isNewQQLogin() ? cookie.get('vqq_vuserid') : cookie.get('vuserid');
}

/**
 * QQLogin openid,access_token换视频票据
 */
export function openNativeLogin() {
  reporter.report({
    f_event: reporter.EVENT_LOGIN_START,
  });
  return requestQQNativeAuth()
    .then(params => requestQQLogin(params)
      .then(() => new Promise((resolve) => {
        syncCookies(resolve);
      })));
}

function _syncAppCookies() {
  return invoke('getMainCookie').then((res) => {
    let cookies = {};
    if (res && res.cookie) {
      cookies = parseCookieString(res.cookie);
      cookies.main_login = res.type;
      if (res.type === 'wx') { // 微信账号添加vusession/vuserid到cookie中
        if (!util.hasOwn(cookies, 'vuserid') && res.vuserid) {
          cookies.vuserid = res.vuserid;
        }
        if (!util.hasOwn(cookies, 'vusession') && res.vusession) {
          cookies.vusession = res.vusession;
        }
      }
    }
    cookie.setCookies(cookies);
  })
    .catch((e) => {
      console.log('[common/login._syncAppCookies]getMainCookie failed in local pkg mode', e);
      cookie.setCookies();
    });
}

// public function
export const DEFAULT_LOGIN = {
  VERSION: version,
  setConfig,
  init,
  onInit,
  loginType,
  isLogin,
  isStrictLogin,
  isPTLogin,
  isNewQQLogin,
  isQQLogin,
  isWXLogin,
  openLogin,
  logout,
  checkUser,
  getMainUserInfo,
  getMainCookie,
  getUin,
  getUid,
  getVuid,
  getToken,
  buildCSRF,
  openNativeLogin,
  loginSource() {
    return 'vip';
  },
};

export default new LoginProxy(DEFAULT_LOGIN);
